import React, { useCallback, useEffect, useState } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Overlay from "components/Overlay/Overlay";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";
import CardDashboardItemTripleData from "components/CardDashboardItemHorizontalMulti";
import DistributionPie from "components/DistributionPie";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";

/**
 * Linguist Numbers — the "numbers" panel for the Linguists list (2026-10-30).
 *
 * WHERE THIS CAME FROM
 * The old Dashboard's "Linguists" overview rendered these exact figures above
 * a duplicate of the Linguists grid. The figures moved here; the duplicate grid
 * did not. Same endpoint (`linguistTotals`), same cards, same donuts.
 *
 * These are directory totals, not the filtered rows on screen — the header
 * says so, because a count that disagrees with the grid underneath is worse
 * than no count at all.
 */
const LinguistStatsOverlay = () => {
  const theme = useTheme();
  const { setOverlayName, handleErrorDialog } = useStateContext();

  const [isLoading, setIsLoading] = useState(true);
  const [totals, setTotals] = useState(null);

  const closeOverlay = () => setOverlayName("");

  const fetchTotals = useCallback(() => {
    setIsLoading(true);

    api
      .get("/dashboard/all/linguists/linguistTotals")
      .then((response) => {
        setTotals(response?.data || null);
        setIsLoading(false);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          "Unable to load the linguist figures. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  const byStatus = [
    {
      id: 0,
      value: Number(totals?.byStatus?.Active) || 0,
      label: "Active",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byStatus?.Inactive) || 0,
      label: "Inactive",
      color: theme.palette.error[400],
    },
    {
      id: 2,
      value: Number(totals?.byStatus?.["N/A"]) || 0,
      label: "N/A",
      color: theme.palette.grey[400],
    },
  ];

  const byCertification = [
    {
      id: 0,
      value: Number(totals?.byCertification?.Certified) || 0,
      label: "Certified",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byCertification?.["Non-certified"]) || 0,
      label: "Non-certified",
      color: theme.palette.error[400],
    },
  ];

  const byContract = [
    {
      id: 0,
      value: Number(totals?.byContract?.Yes) || 0,
      label: "Have Contract",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byContract?.No) || 0,
      label: "Need Contract",
      color: theme.palette.error[400],
    },
  ];

  const byW9 = [
    {
      id: 0,
      value: Number(totals?.byW9?.Yes) || 0,
      label: "Have W9",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byW9?.No) || 0,
      label: "Need W9",
      color: theme.palette.error[400],
    },
  ];

  const byBadge = [
    {
      id: 0,
      value: Number(totals?.byBadge?.Yes) || 0,
      label: "Have Badge",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(totals?.byBadge?.No) || 0,
      label: "Need Badge",
      color: theme.palette.error[400],
    },
  ];

  return (
    <Overlay
      id="linguist-numbers"
      open={true}
      onClose={closeOverlay}
      title="Linguist Numbers"
      maxWidthPx={1400}
      // 2026-11-03 (Amine): wrap the content — a fixed-height shell left a
      // large empty band under the figures.
      height="fit-content"
    >
      <Box sx={{ p: { xs: 1, sm: 2 }, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 0.5 }}>
          Every linguist on record — this is the whole directory, not the rows
          currently filtered in the list.
        </Typography>

        <CardDashboardItemTripleData
          titles={["Total Linguists", "Total Languages"]}
          contents={[totals?.totalLinguists, totals?.totalLanguages]}
          isLoading={isLoading}
        />

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
            <CardDashboardKPIChart
              title="Linguists By Status"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={<DistributionPie data={byStatus} seriesId="Status" />}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
            <CardDashboardKPIChart
              title="Linguists By Certification"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={
                <DistributionPie data={byCertification} seriesId="Certification" />
              }
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
            <CardDashboardKPIChart
              title="Linguists By Contract"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={<DistributionPie data={byContract} seriesId="Contract" />}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
            <CardDashboardKPIChart
              title="Linguists By Badge"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={<DistributionPie data={byBadge} seriesId="Badge" />}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
            <CardDashboardKPIChart
              title="Linguists By W9"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={<DistributionPie data={byW9} seriesId="W9" />}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        {isLoading ? (
          <Skeleton
            variant="rounded"
            height={40}
            sx={{ mt: 2, borderRadius: "12px" }}
          />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, px: 0.5 }}
          >
            Need performance, earnings, compliance history or preferred-linguist
            analysis? Those live in Reports → Linguists.
          </Typography>
        )}
      </Box>
    </Overlay>
  );
};

export default LinguistStatsOverlay;
